import { useMutation } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type { TApiODIN } from "@/apis/odin/apiODIN";
import type { TSliceNotifications } from "@/store/notifications/sliceNotifications";
import { userKeys, projectKeys } from "@/queries/queryKeys";

export const createDatabaseMutationHooks = ({
    apiODIN,
    queryClient,
    addNotificationWithDefaultTimeout,
}: {
    apiODIN: TApiODIN;
    queryClient: QueryClient;
    addNotificationWithDefaultTimeout: TSliceNotifications["addNotificationWithDefaultTimeout"];
}) => {
    const useSetSelectedDatabasesMutation = () =>
        useMutation({
            mutationFn: async ({
                selectedDatabaseIds,
                notifyUser = false,
            }: {
                selectedDatabaseIds: number[];
                notifyUser?: boolean;
            }) => {
                await apiODIN.setUserSelectedDatabases(selectedDatabaseIds);
                if (notifyUser) {
                    try {
                        const databaseMetadata =
                            await apiODIN.getDatabaseMetadata(
                                selectedDatabaseIds,
                            );
                        const databaseNames = databaseMetadata.data.map(
                            database =>
                                `${database.databaseCompanyName} | ${database.databaseName}`,
                        );
                        addNotificationWithDefaultTimeout({
                            title: `Switched ${databaseNames.length} database${
                                databaseNames.length === 1 ? "" : "s"
                            }`,
                            message: databaseNames.join("\n"),
                            severity: "success",
                            kind: "toast",
                        });
                    } catch (error) {
                        console.error(error);
                        addNotificationWithDefaultTimeout({
                            title: `Switched databases`,
                            severity: "success",
                            kind: "toast",
                        });
                    }
                }
            },
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: userKeys.all,
                });
                void queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: `Could not switch databases`,
                    severity: "danger",
                    kind: "toast",
                });
            },
        });

    return {
        useSetSelectedDatabasesMutation,
    };
};

export type TDatabaseMutationHooks = ReturnType<
    typeof createDatabaseMutationHooks
>;
